import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDropzone } from 'react-dropzone';
import {
  Image,
  FileText,
  FileArchive,
  FileCode,
  Download,
  X as XIcon,
  Loader2,
  Upload,
  Paperclip
} from 'lucide-react';
import { SignedUrl } from '../shared/SignedUrl';
import type { NoteAttachment } from '../../lib/types';

interface NoteAttachmentsProps {
  noteId?: string; // Optional in pre-creation mode
  attachments: NoteAttachment[];
  onUpload: (noteId: string, file: File) => Promise<NoteAttachment | null>;
  onDelete: (attachmentId: string) => Promise<boolean>;
  disabled?: boolean;
  toast?: {
    success: (message: string) => void;
    error: (message: string) => void;
  };
  // Pre-creation mode: collect files without uploading
  preCreationMode?: boolean;
  onFilesSelected?: (files: File[]) => void;
  selectedFiles?: File[];
  /**
   * 'default' (invariato): dropzone tratteggiata, usata da LeadNotesSection/DealNotesSection/
   * PartnerNoteCard. 'ghost-row': riga ghost "Allega file" (drawer condiviso Nota/Attività,
   * Task 4 redesign) — cambia SOLO il render dell'area di drop, react-dropzone invariato
   * (drop resta attivo sull'area della riga, click apre il file picker).
   */
  variant?: 'default' | 'ghost-row';
}

export const NoteAttachments: React.FC<NoteAttachmentsProps> = ({
  noteId,
  attachments,
  onUpload,
  onDelete,
  disabled = false,
  toast,
  preCreationMode = false,
  onFilesSelected,
  selectedFiles = [],
  variant = 'default'
}) => {
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, number>>({});
  const [previewFile, setPreviewFile] = useState<{ url: string; type: string } | null>(null);

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <Image className="h-5 w-5 text-blue-500" />;
    if (type.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
    if (type.includes('zip') || type.includes('rar')) return <FileArchive className="h-5 w-5 text-yellow-500" />;
    if (type.includes('html') || type.includes('javascript') || type.includes('css')) return <FileCode className="h-5 w-5 text-green-500" />;
    return <FileText className="h-5 w-5 text-gray-500" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (disabled) return;

    // Pre-creation mode: just collect files without uploading
    if (preCreationMode) {
      if (onFilesSelected) {
        const newFiles = [...selectedFiles, ...acceptedFiles];
        onFilesSelected(newFiles);
      }
      return;
    }

    // Normal mode: upload immediately
    if (!noteId) return;

    for (const file of acceptedFiles) {
      try {
        setUploadingFiles(prev => ({ ...prev, [file.name]: 0 }));

        // Simulate progress (since Supabase doesn't provide upload progress easily)
        const progressInterval = setInterval(() => {
          setUploadingFiles(prev => {
            const current = prev[file.name] || 0;
            if (current >= 90) {
              clearInterval(progressInterval);
              return prev;
            }
            return { ...prev, [file.name]: current + 10 };
          });
        }, 200);

        const attachment = await onUpload(noteId, file);

        clearInterval(progressInterval);

        if (attachment) {
          setUploadingFiles(prev => {
            const newState = { ...prev };
            delete newState[file.name];
            return newState;
          });
          toast?.success(`File caricato: ${file.name}`);
        } else {
          setUploadingFiles(prev => {
            const newState = { ...prev };
            delete newState[file.name];
            return newState;
          });
          toast?.error(`Errore nel caricamento di: ${file.name}`);
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        setUploadingFiles(prev => {
          const newState = { ...prev };
          delete newState[file.name];
          return newState;
        });
        toast?.error(`Errore nel caricamento di: ${file.name}`);
      }
    }
  }, [noteId, onUpload, disabled, toast, preCreationMode, onFilesSelected, selectedFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled,
    maxSize: 50 * 1024 * 1024 // 50MB
  });

  const handleDelete = async (attachmentId: string, fileName: string) => {
    if (disabled) return;

    try {
      const success = await onDelete(attachmentId);
      if (success) {
        toast?.success(`File eliminato: ${fileName}`);
      } else {
        toast?.error(`Errore nell'eliminazione di: ${fileName}`);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast?.error(`Errore nell'eliminazione di: ${fileName}`);
    }
  };

  const handleRemoveSelectedFile = (fileToRemove: File) => {
    if (onFilesSelected) {
      const newFiles = selectedFiles.filter(f => f !== fileToRemove);
      onFilesSelected(newFiles);
    }
  };

  return (
    <div className="mt-3 space-y-2">
      {/* Drag & Drop Area - Hidden when disabled */}
      {!disabled && variant === 'ghost-row' && (
        <div
          {...getRootProps()}
          className="inline-flex items-center gap-1.5 text-xs text-[#787774] dark:text-gray-400 px-2 py-1.5 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] cursor-pointer self-start"
        >
          <input {...getInputProps()} />
          <Paperclip className="h-3.5 w-3.5" strokeWidth={1.5} />
          <span>{isDragActive ? 'Rilascia i file qui...' : 'Allega file'}</span>
        </div>
      )}
      {!disabled && variant === 'default' && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-3 transition-colors ${
            isDragActive
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
          } cursor-pointer hover:border-gray-400 dark:hover:border-gray-500`}
        >
          <input {...getInputProps()} />
          <div className="flex items-center justify-center text-sm text-gray-600 dark:text-gray-400">
            <Upload className="h-4 w-4 mr-2" />
            {isDragActive ? (
              <span>Rilascia i file qui...</span>
            ) : (
              <span>Trascina i file qui o clicca per selezionare</span>
            )}
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {Object.entries(uploadingFiles).length > 0 && (
        <div className="space-y-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              Caricamento in corso...
            </span>
          </div>
          {Object.entries(uploadingFiles).map(([fileName, progress]) => (
            <div key={fileName} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-700 dark:text-gray-300 truncate flex-1 mr-2">{fileName}</span>
                <span className="text-blue-600 dark:text-blue-400 font-semibold">{Math.round(progress)}%</span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#0f6cbd] transition-all duration-300 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Selected Files (Pre-creation mode) */}
      {preCreationMode && selectedFiles.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            File selezionati ({selectedFiles.length})
          </div>
          {selectedFiles.map((file, index) => (
            <div
              key={`selected-${index}`}
              className="flex items-center justify-between p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-xs"
            >
              <div className="flex items-center min-w-0 flex-1">
                {getFileIcon(file.type)}
                <span className="ml-2 text-gray-900 dark:text-gray-100 truncate">{file.name}</span>
                <span className="ml-2 text-gray-500 dark:text-gray-400">({formatFileSize(file.size)})</span>
              </div>
              <button
                onClick={() => handleRemoveSelectedFile(file)}
                className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors p-1"
                title="Rimuovi file"
              >
                <XIcon className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Attachments List (Already uploaded) */}
      {attachments && attachments.length > 0 && (
        <div className="space-y-1">
          {attachments.map((file) => (
            <SignedUrl key={file.id} url={file.url}>
            {(href) => (
            <div
              className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs cursor-pointer hover:bg-gray-700/50 dark:hover:bg-gray-700 transition-colors"
              onClick={() => {
                // Solo immagini nella lightbox, altri file in nuova scheda
                if ((file.type || '').startsWith('image/')) {
                  setPreviewFile({ url: href ?? file.url, type: file.type || '' });
                } else {
                  window.open(href ?? file.url, '_blank');
                }
              }}
            >
              <div className="flex items-center min-w-0 flex-1">
                {getFileIcon(file.type || '')}
                <span className="ml-2 text-gray-900 dark:text-gray-100 truncate">{file.name}</span>
                {file.size && (
                  <span className="ml-2 text-gray-500 dark:text-gray-400">({formatFileSize(file.size)})</span>
                )}
              </div>
              <div className="flex items-center gap-1 ml-2">
                <a
                  href={href ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1"
                  onClick={(e) => e.stopPropagation()}
                  download
                  title="Scarica file"
                >
                  <Download className="h-3 w-3" />
                </a>
                {!disabled && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(file.id, file.name);
                    }}
                    className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors p-1"
                    title="Elimina file"
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
            )}
            </SignedUrl>
          ))}
        </div>
      )}

      {/* Image Lightbox - Solo per immagini */}
      {previewFile && previewFile.type.startsWith('image/') && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99999,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setPreviewFile(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setPreviewFile(null)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 100000,
              padding: 12,
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <XIcon style={{ width: 24, height: 24, color: 'white' }} />
          </button>

          {/* Download button */}
          <a
            href={previewFile.url}
            download
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: 16,
              right: 80,
              zIndex: 100000,
              padding: 12,
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
            }}
          >
            <Download style={{ width: 24, height: 24, color: 'white' }} />
          </a>

          {/* Image */}
          <img
            src={previewFile.url}
            alt="Preview"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 8,
            }}
          />
        </div>,
        document.body
      )}
    </div>
  );
};
